import { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, BadgeDollarSign, Loader2, X, Copy, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";

const inputCls = "w-full bg-[#0B0F17] border border-slate-700 focus:border-amber-500 outline-none px-4 py-3 text-slate-100 placeholder:text-slate-600 transition-colors duration-300";
const labelCls = "block text-xs font-mono-brand uppercase tracking-[0.2em] text-slate-400 mb-2";

const STEPS = ["Service", "Schedule", "Details", "Confirm"];

export const BookingFlow = ({ services, slots, selectedService, onSelectService }) => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [takenSlots, setTakenSlots] = useState([]);
  const [dayBlocked, setDayBlocked] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({
    date: "", time_slot: "", name: "", email: "", phone: "", address: "", description: "", frequency: "once",
  });

  useEffect(() => {
    if (!form.date) return;
    api.get("/availability", { params: { date: form.date } })
      .then((r) => {
        if (r.data.day_blocked) {
          setDayBlocked(r.data.reason || "We're not working that day");
          setForm((f) => ({ ...f, date: "", time_slot: "" }));
          setTakenSlots([]);
          return;
        }
        setDayBlocked(null);
        setTakenSlots(r.data.taken_slots);
        setForm((f) => (r.data.taken_slots.includes(f.time_slot) ? { ...f, time_slot: "" } : f));
      })
      .catch(() => setTakenSlots([]));
  }, [form.date]);

  const service = useMemo(() => services.find((s) => s.slug === selectedService), [services, selectedService]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const today = new Date().toISOString().split("T")[0];

  const canNext =
    (step === 0 && !!service) ||
    (step === 1 && form.date && form.time_slot) ||
    (step === 2 && form.name.trim() && form.email.trim() && form.phone.trim() && form.address.trim());

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", { service_slug: service.slug, ...form });
      let photosSent = 0;
      if (photos.length) {
        try {
          const fd = new FormData();
          photos.slice(0, 3).forEach((f) => fd.append("files", f));
          const pr = await api.post(`/bookings/${r.data.booking.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          photosSent = pr.data.photos.length;
        } catch {
          toast.error("Booking saved, but the photos didn't upload — text them to 0480 573 604 instead.");
        }
      }
      setResult({ ...r.data, photos_sent: photosSent });
      setPhotos([]);
      toast.success("Booking received!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setStep(0);
    setForm({ date: "", time_slot: "", name: "", email: "", phone: "", address: "", description: "", frequency: "once" });
  };

  return (
    <section id="booking" data-testid="booking-section" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-12 max-w-4xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="mb-12 text-left">
        <p className="text-xs font-mono-brand uppercase tracking-[0.25em] text-amber-400 mb-4">04 — Book Online</p>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-condensed font-extrabold uppercase tracking-tight leading-none">
          Lock In <span className="text-amber-500">Your Job</span>
        </h2>
        <p className="mt-4 text-slate-400 text-sm flex items-center gap-2">
          <Phone className="w-4 h-4 text-amber-500" />
          Prefer to talk it through? Call or text
          <a href="tel:+61480573604" data-testid="booking-phone-link" className="text-amber-400 hover:text-amber-300 font-mono-brand transition-colors">0480 573 604</a>
        </p>
      </motion.div>

      <div className="flex items-center gap-2 mb-10" data-testid="booking-progress">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2 flex-1">
            <span className={`w-8 h-8 shrink-0 flex items-center justify-center font-condensed font-bold text-sm border transition-colors duration-300 ${i < step ? "bg-amber-500 text-slate-950 border-amber-500" : i === step ? "border-amber-500 text-amber-400" : "border-slate-700 text-slate-600"}`}>
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </span>
            <span className={`hidden sm:block text-xs font-mono-brand uppercase tracking-[0.15em] ${i <= step ? "text-slate-300" : "text-slate-600"}`}>{label}</span>
            {i < STEPS.length - 1 && <span className="flex-1 h-px bg-slate-800" />}
          </div>
        ))}
      </div>

      <div className="bg-[#0F172A] border border-amber-500/15 p-6 sm:p-10 min-h-[380px]">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
            {step === 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="booking-step-service">
                {services.map((s) => (
                  <button key={s.slug} onClick={() => onSelectService(s.slug)} data-testid={`booking-service-${s.slug}`}
                          className={`text-left p-4 border transition-all duration-300 ${selectedService === s.slug ? "border-amber-500 bg-amber-500/10" : "border-slate-700 hover:border-amber-500/50"}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-condensed font-bold uppercase tracking-wide text-slate-100">{s.name}</span>
                      <span className="text-xs font-mono-brand text-amber-400">{s.estimate}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {step === 1 && (
              <div data-testid="booking-step-schedule">
                <label className={labelCls}>Preferred Date</label>
                <input type="date" min={today} value={form.date} onChange={set("date")} data-testid="booking-date-input"
                       className={`${inputCls} mb-2 [color-scheme:dark]`} />
                {dayBlocked && (
                  <p className="text-sm text-amber-400 mb-4" data-testid="booking-day-blocked">
                    Not available that day ({dayBlocked}) — please pick another date.
                  </p>
                )}
                <label className={labelCls}>Preferred Time</label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {slots.map((t) => {
                    const taken = takenSlots.includes(t);
                    return (
                      <button key={t} disabled={taken} onClick={() => setForm((f) => ({ ...f, time_slot: t }))} data-testid={`booking-slot-${t.replace(/[^0-9a-z]/gi, "-").toLowerCase()}`}
                              className={`py-3 text-sm font-mono-brand border transition-all duration-300 ${taken ? "border-slate-800 text-slate-700 line-through cursor-not-allowed" : form.time_slot === t ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-slate-700 text-slate-300 hover:border-amber-500/50"}`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
                {form.date && takenSlots.length === slots.length && (
                  <p className="text-sm text-amber-400 mt-4" data-testid="booking-day-full">Fully booked that day — please pick another date.</p>
                )}
                {service && ["lawn-mowing", "garden-weeding"].includes(service.slug) && (
                  <div className="mt-8" data-testid="booking-frequency">
                    <label className={labelCls}>One-Off Or Regular?</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[["once", "One-Off", "Just this visit"], ["weekly", "Weekly ×8", "8 visits, every week"], ["fortnightly", "Fortnightly ×8", "8 visits, every 2 weeks"]].map(([val, label, hint]) => (
                        <button key={val} onClick={() => setForm((f) => ({ ...f, frequency: val }))} data-testid={`booking-frequency-${val}`}
                                className={`p-4 text-left border transition-all duration-300 ${form.frequency === val ? "border-amber-500 bg-amber-500/10" : "border-slate-700 hover:border-amber-500/50"}`}>
                          <span className={`block font-condensed font-bold uppercase tracking-wide ${form.frequency === val ? "text-amber-400" : "text-slate-200"}`}>{label}</span>
                          <span className="block text-xs text-slate-500 mt-1">{hint}</span>
                        </button>
                      ))}
                    </div>
                    {form.frequency !== "once" && (
                      <p className="text-xs text-slate-500 mt-3" data-testid="booking-frequency-note">
                        We'll book 8 {form.frequency} visits at this day and time. Any dates already taken get skipped and we'll sort make-up times by phone.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            {step === 2 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="booking-step-details">
                <div>
                  <label className={labelCls}>Full Name *</label>
                  <input value={form.name} onChange={set("name")} placeholder="Jane Citizen" data-testid="booking-name-input" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Phone *</label>
                  <input value={form.phone} onChange={set("phone")} placeholder="0400 000 000" data-testid="booking-phone-input" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Email *</label>
                  <input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="booking-email-input" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Job Address *</label>
                  <input value={form.address} onChange={set("address")} placeholder="12 Example St, Your Suburb" data-testid="booking-address-input" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Describe The Job</label>
                  <textarea rows={3} value={form.description} onChange={set("description")} data-testid="booking-description-input"
                            placeholder="e.g. Front and back lawn, edges need doing, one side gate to fix…" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Photos Of The Job (optional — helps us quote)</label>
                  <input type="file" accept="image/*" multiple data-testid="booking-photos-input"
                         onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, 3))}
                         className="w-full text-xs text-slate-400 file:mr-3 file:bg-amber-500 file:text-slate-950 file:border-0 file:px-4 file:py-2.5 file:font-mono-brand file:text-xs file:uppercase file:cursor-pointer file:hover:bg-amber-600" />
                  <p className="text-xs text-slate-600 mt-2" data-testid="booking-photos-note">
                    {photos.length > 0 ? `${photos.length} photo(s) ready to send with your booking` : "Up to 3 photos — show us the overgrown jungle, the leaning fence, the stained driveway…"}
                  </p>
                </div>
              </div>
            )}
            {step === 3 && service && (
              <div data-testid="booking-step-confirm">
                <div className="border border-amber-500/30 divide-y divide-slate-800 mb-6">
                  {[
                    ["Service", service.name],
                    ["Estimate", service.estimate],
                    ...(form.frequency !== "once" ? [["Repeats", form.frequency === "weekly" ? "Weekly — 8 visits" : "Fortnightly — 8 visits"]] : []),
                    ["Date", form.date],
                    ["Time", form.time_slot],
                    ["Name", form.name],
                    ["Phone", form.phone],
                    ["Email", form.email],
                    ["Address", form.address],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 px-4 py-3">
                      <span className="text-xs font-mono-brand uppercase tracking-[0.15em] text-slate-500">{k}</span>
                      <span className="text-sm text-slate-200 text-right">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/40 px-4 py-3">
                  <BadgeDollarSign className="w-5 h-5 text-amber-400 shrink-0" />
                  <p className="text-sm text-amber-200" data-testid="pay-at-site-notice">
                    No online payment. You pay directly at the job site when the work is done.
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-10">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="booking-back-button"
                  className="inline-flex items-center gap-2 text-sm font-mono-brand uppercase tracking-[0.15em] text-slate-400 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          {step < 3 ? (
            <button onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext} data-testid="booking-next-button"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-condensed font-bold uppercase tracking-wide px-8 py-3 transition-all duration-300">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="booking-submit-button"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-condensed font-bold uppercase tracking-wide px-8 py-3 transition-all duration-300">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {submitting ? "Booking…" : "Confirm Booking"}
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 z-[60] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4"
                      data-testid="booking-confirmation-modal">
            <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
                        className="bg-[#0F172A] border border-amber-500/40 max-w-md w-full p-8 relative">
              <button onClick={reset} data-testid="confirmation-close-button" aria-label="Close"
                      className="absolute top-4 right-4 text-slate-500 hover:text-slate-200">
                <X className="w-5 h-5" />
              </button>
              <div className="w-3 h-3 hazard-stripes mb-6" />
              <p className="text-xs font-mono-brand uppercase tracking-[0.25em] text-amber-400 mb-2">Booking Received</p>
              <h3 className="text-3xl font-condensed font-extrabold uppercase mb-1">You're Locked In</h3>
              <button onClick={() => { navigator.clipboard?.writeText(result.booking.id); toast.success("Booking ID copied"); }}
                      data-testid="confirmation-id" className="inline-flex items-center gap-2 text-sm font-mono-brand text-amber-400 hover:text-amber-300 mb-6">
                #{result.booking.id.slice(0, 8).toUpperCase()} <Copy className="w-3.5 h-3.5" />
              </button>
              <div className="text-sm text-slate-300 space-y-1.5 mb-6">
                <p><span className="text-slate-500">Service:</span> {result.booking.service_name} ({result.booking.estimate})</p>
                <p><span className="text-slate-500">When:</span> {result.booking.date} at {result.booking.time_slot}</p>
                <p><span className="text-slate-500">Where:</span> {result.booking.address}</p>
                {result.series && result.series.visits > 1 && (
                  <p className="text-amber-400" data-testid="confirmation-series">
                    {result.series.visits} {result.series.frequency} visits locked in
                    {result.series.skipped_dates.length > 0 && ` — ${result.series.skipped_dates.length} taken date(s) skipped`}
                  </p>
                )}
                {result.photos_sent > 0 && (
                  <p className="text-amber-400" data-testid="confirmation-photos">
                    {result.photos_sent} photo(s) sent — we'll quote from those
                  </p>
                )}
              </div>
              <p className="text-xs text-slate-500 mb-6" data-testid="confirmation-email-note">
                {result.email_sent
                  ? `A confirmation email is on its way to ${result.booking.email}. We'll call to confirm your exact time.`
                  : "We'll be in touch shortly to confirm your exact time."}
              </p>
              <div className="flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 px-4 py-3">
                <BadgeDollarSign className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="text-xs font-mono-brand uppercase tracking-[0.15em] text-amber-300">Pay directly at job site — nothing due now</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
